import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PatientControllerApi } from '../api/generated';
import type { PatientResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { getApiErrorMessage } from '../utils/errorMessageUtils';

export interface UsePatientsResult {
  patients: PatientResponse[];
  isLoading: boolean;
  loadError: string | null;
  refresh: () => Promise<void>;
}

/** Read-only patient list for the active location, e.g. for patient pickers. */
export function usePatients(locationId: string | null): UsePatientsResult {
  const { t } = useTranslation();
  const [patients, setPatients] = useState<PatientResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const api = useMemo(() => new PatientControllerApi(createApiConfiguration()), []);

  const refresh = useCallback(async () => {
    if (!locationId) {
      setPatients([]);
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await api.listPatients({ locationId });
      setPatients(response.data);
    } catch (e) {
      setLoadError(getApiErrorMessage(e, t('fetch-error')));
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, [api, locationId, t]);

  useEffect(() => {
    if (!locationId) return;
    refresh().catch(() => {
      // load errors are already surfaced via loadError state
    });
  }, [locationId, refresh]);

  return { patients, isLoading, loadError, refresh };
}
